import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FiX, FiMaximize2, FiTrash2 } from 'react-icons/fi';
import KindSearchSelect from './KindSearchSelect.jsx';
import { isUserDrawnComponent } from '../../utils/userMarkup.js';
import {
  HATCH_PATTERN_OPTIONS,
  isFillableUserMarkup,
  normalizeMarkupStyle,
} from '../../utils/markupStyle.js';
import { suggestKindsForUserMarkup } from '../../utils/drawIntentSuggest.js';
import {
  formatAreaSqFt,
  formatFeetInches,
  pdfAreaToRealSqFeet,
  pdfPointsToRealFeet,
} from '../../utils/drawingMeasurements.js';
import { DIMENSIONAL_OUTPUT_KINDS_LC } from '../../constants/drawingTaxonomy';

function formatMarkupMeasurements(measurements, scaleRatio) {
  if (!measurements?.lengthPt) return null;
  if (scaleRatio) {
    const lenFt = pdfPointsToRealFeet(measurements.lengthPt, scaleRatio);
    const lenStr = formatFeetInches(lenFt);
    if (measurements.areaSqPt) {
      const sqFt = pdfAreaToRealSqFeet(measurements.areaSqPt, scaleRatio);
      if (sqFt != null) return `${lenStr} · ${formatAreaSqFt(sqFt)}`;
    }
    return lenStr;
  }
  return `${measurements.lengthPt.toFixed(0)} pt${
    measurements.areaSqPt ? ` · ${measurements.areaSqPt.toFixed(0)} sq pt` : ''
  }`;
}

const Card = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 15;
  width: min(300px, calc(100% - 80px));
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.18);
  border: 1px solid rgba(0, 0, 0, 0.08);
  pointer-events: auto;
`;

const PromptBanner = styled.div`
  margin-bottom: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(233, 112, 36, 0.12);
  border: 1px solid rgba(233, 112, 36, 0.35);
  font-size: 11px;
  font-weight: 600;
  color: #9a3412;
  line-height: 1.45;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
`;

const Title = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  line-height: 1.35;
`;

const Sub = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 2px;
`;

const IconBtn = styled.button`
  border: none;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const Label = styled.div`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  color: #9ca3af;
  margin-bottom: 4px;
`;

const SuggestRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
`;

const Chip = styled.button`
  border: 1px solid rgba(233, 112, 36, 0.35);
  background: rgba(233, 112, 36, 0.08);
  color: #c45f12;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const SaveBtn = styled.button`
  flex: 1;
  border: none;
  border-radius: 8px;
  padding: 8px 10px;
  background: #e97024;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const GhostBtn = styled.button`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 8px 10px;
  background: #fff;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const AppearanceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 10px;
  margin-top: 8px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FieldWide = styled(Field)`
  grid-column: 1 / -1;
`;

const MiniLabel = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
`;

const ColorInput = styled.input`
  width: 100%;
  height: 32px;
  padding: 2px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  cursor: pointer;
`;

const RangeInput = styled.input`
  width: 100%;
`;

const SelectInput = styled.select`
  width: 100%;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 12px;
  background: #fff;
`;

const RangeValue = styled.span`
  font-size: 10px;
  color: #9ca3af;
  margin-left: 4px;
`;

function defaultAxisForKind(kind) {
  if (!kind) return 'reference_annotation';
  if (DIMENSIONAL_OUTPUT_KINDS_LC.has(kind.toLowerCase())) return 'dimensional_output';
  return 'reference_annotation';
}

export default function FloatingMarkupInspector({
  component,
  scaleRatio = null,
  intentPrompt = false,
  onSave,
  onDelete,
  onDismiss,
  onOpenFullInspector,
}) {
  const [kind, setKind] = useState('');
  const [labelText, setLabelText] = useState('');
  const [style, setStyle] = useState(() => normalizeMarkupStyle());

  const componentId = component?.id;
  const componentKind = component?.kind;
  const componentLabel = component?.label;
  const componentStyle = component?.style;

  useEffect(() => {
    if (!componentId) return;
    setKind(componentKind || '');
    setLabelText(componentLabel || '');
  }, [componentId, componentKind, componentLabel]);

  useEffect(() => {
    if (!componentId) return;
    setStyle(normalizeMarkupStyle(componentStyle));
  }, [componentId, componentStyle]);

  const suggestions = useMemo(() => {
    if (!component) return [];
    return suggestKindsForUserMarkup(component);
  }, [component]);

  if (!component || !isUserDrawnComponent(component)) return null;

  const fillable = isFillableUserMarkup(component);

  const canSave = Boolean(kind) && (
    kind !== component.kind || labelText !== (component.label || '')
  );

  const applyStylePatch = (patch) => {
    const nextStyle = normalizeMarkupStyle({ ...style, ...patch });
    setStyle(nextStyle);
    onSave?.(component.id, { style: nextStyle });
  };

  const handleSave = () => {
    if (!kind) return;
    onSave?.(component.id, {
      kind,
      taxonomyAxis: defaultAxisForKind(kind),
      label: labelText || kind,
      value: labelText || kind,
      mappingStatus: 'user_confirmed',
    });
  };

  const m = component.measurements || {};
  const measureHint = formatMarkupMeasurements(m, scaleRatio);

  return (
    <Card>
      {intentPrompt && (
        <PromptBanner>
          New markup — choose an intent (kind) below, then Save intent.
        </PromptBanner>
      )}
      <Header>
        <div>
          <Title>{intentPrompt ? 'Assign intent' : 'User object'}</Title>
          <Sub>
            {intentPrompt
              ? 'Pick a kind or tap a suggestion chip below'
              : 'Drag vertices · Shift+click open corner on L-shape to complete rectangle · Shift+click vertex = delete'}
            {!intentPrompt && measureHint ? ` · ${measureHint}` : ''}
          </Sub>
        </div>
        <div style={{ display: 'flex', gap: 2 }}>
          {onOpenFullInspector && (
            <IconBtn type="button" title="Open full inspector" onClick={onOpenFullInspector}>
              <FiMaximize2 size={16} />
            </IconBtn>
          )}
          {!intentPrompt && (
            <IconBtn type="button" title="Dismiss" onClick={onDismiss}>
              <FiX size={16} />
            </IconBtn>
          )}
        </div>
      </Header>

      <Label>Intent (kind)</Label>
      <KindSearchSelect
        value={kind}
        onChange={setKind}
      />
      {suggestions.length > 0 && (
        <SuggestRow>
          {suggestions.slice(0, 4).map((s) => (
            <Chip key={s} type="button" onClick={() => setKind(s)}>{s}</Chip>
          ))}
        </SuggestRow>
      )}

      <div style={{ marginTop: 10 }}>
        <Label>Display text</Label>
        <input
          value={labelText}
          onChange={(e) => setLabelText(e.target.value)}
          placeholder="Optional label"
          style={{
            width: '100%',
            height: 32,
            padding: '0 8px',
            border: '1px solid #d1d5db',
            borderRadius: 6,
            fontSize: 12,
          }}
        />
      </div>

      {fillable && (
        <div style={{ marginTop: 12 }}>
          <Label>Appearance</Label>
          <AppearanceGrid>
            <Field>
              <MiniLabel>Stroke</MiniLabel>
              <ColorInput
                type="color"
                value={style.strokeColor}
                onChange={(e) => applyStylePatch({ strokeColor: e.target.value })}
              />
            </Field>
            <Field>
              <MiniLabel>
                Line weight
                <RangeValue>{style.strokeWidth}px</RangeValue>
              </MiniLabel>
              <RangeInput
                type="range"
                min={1}
                max={6}
                step={1}
                value={style.strokeWidth}
                onChange={(e) => applyStylePatch({ strokeWidth: Number(e.target.value) })}
              />
            </Field>
            <Field>
              <MiniLabel>Fill</MiniLabel>
              <ColorInput
                type="color"
                value={style.fillColor}
                onChange={(e) => applyStylePatch({ fillColor: e.target.value })}
              />
            </Field>
            <Field>
              <MiniLabel>
                Fill opacity
                <RangeValue>{Math.round(style.fillOpacity * 100)}%</RangeValue>
              </MiniLabel>
              <RangeInput
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={style.fillOpacity}
                onChange={(e) => applyStylePatch({ fillOpacity: Number(e.target.value) })}
              />
            </Field>
            <FieldWide>
              <MiniLabel>Hatch / fill</MiniLabel>
              <SelectInput
                value={style.hatchPattern}
                onChange={(e) => applyStylePatch({ hatchPattern: e.target.value })}
              >
                {HATCH_PATTERN_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.id}>{opt.label}</option>
                ))}
              </SelectInput>
            </FieldWide>
          </AppearanceGrid>
        </div>
      )}

      <Actions>
        <SaveBtn type="button" disabled={!canSave} onClick={handleSave}>
          Save intent
        </SaveBtn>
        <GhostBtn type="button" onClick={() => onDelete?.(component.id)} title="Delete">
          <FiTrash2 size={14} />
        </GhostBtn>
      </Actions>
    </Card>
  );
}
